import { useGlobals }                                                   from '@/hooks/use-globals';
import { Computed }                                                     from '@/types';
import { Season, SeasonDate, SeasonDateWithCode, SeasonEventSelection } from '@/views/seasons/seasons.types';
import { computed, Ref }                                                from '@vue/composition-api';
import { changeColorInHex }                                             from '@/styles/variables.helpers';
import { differenceInDays, getDaysInMonth }                             from 'date-fns';

export interface UseSeasonsDay {
  selection: Computed<SeasonEventSelection>;
  updateCalendar: (payload: SeasonDateWithCode) => void;
  updateSelection: (selection: Partial<SeasonEventSelection>) => void;
  getDatesBasedOnRange: (start: SeasonDate, end: SeasonDate) => SeasonDate[];
  getLabel: (day: number) => string;
  getActiveDay: () => string | null;
  getClasses: () => string[] | null;
  getSelectionClasses: () => string[];
  getParentClass: () => string;
}

export const useSeasonsDay = (props: { month: number; day: number }, seasonDay: Ref<HTMLElement | null>): UseSeasonsDay => {
  const { store } = useGlobals();
  const { vSeasons } = store.state.views;
  const selection: Computed<SeasonEventSelection> = computed(() => vSeasons.selection);
  const getActiveDay = (): string | null => (selection.value.seasonCode ? 'c-seasons-day--active' : null);

  const updateSelection = (selection: Partial<SeasonEventSelection>): void => {
    store.commit('views/vSeasons/updateSelection', selection);
  };

  const getSeason = (month: number, day: number): Season | null => {
    const seasonCode: string | null = vSeasons.calendar[ month ][ day ];
    if (seasonCode) {
      const season: Season | undefined = vSeasons.seasons.find((season: Season) => season.code === seasonCode);
      return season || null;
    }
    return null;
  };

  const getLabel = (day: number): string => {
    const dayAsString = day.toString();
    if (dayAsString.length === 1) {
      return `0${dayAsString}`;
    }
    return dayAsString;
  };

  const setColorVariable = (color: string): void => {
    if (seasonDay?.value) {
      const lighten = true;
      seasonDay.value.style.setProperty('--season-color', changeColorInHex(color, 30, !lighten));
      seasonDay.value.style.setProperty('--season-color-lighter', changeColorInHex(color, 30, lighten));
    }
  };

  const isPreviousSeasonEqualCurrent = (): boolean => {
    const season: Season | null = getSeason(props.month, props.day);
    const previousSeason: Season | null = getSeason(props.month, (props.day - 1));
    let previousSeasonPreviousMonth: Season | null = null;

    if (props.month !== 0) {
      previousSeasonPreviousMonth = getSeason((props.month - 1), (vSeasons.calendar[ props.month - 1 ]?.length));
    }
    if (season && previousSeason) {
      return season.code === previousSeason.code;
    }
    if (season && previousSeasonPreviousMonth) {
      return season.code === previousSeasonPreviousMonth.code;
    }
    return false;
  };

  const isNextSeasonEqualCurrent = (): boolean => {
    const season: Season | null = getSeason(props.month, props.day);
    const nextSeason: Season | null = getSeason(props.month, (props.day + 1));
    let nextSeasonNextMonth: Season | null = null;

    if (props.month !== 11) {
      nextSeasonNextMonth = getSeason((props.month + 1), (vSeasons.calendar[ props.month + 1 ]?.length));
    }
    if (season && nextSeason) {
      return season.code === nextSeason.code;
    }
    if (season && nextSeasonNextMonth) {
      return season.code === nextSeasonNextMonth.code;
    }
    return false;
  };

  const getClasses = (): string[] | null => {
    const season: Season | null = getSeason(props.month, props.day);

    if (season) {
      setColorVariable(season.color);
      const classes: string[] = [];

      if (isPreviousSeasonEqualCurrent() && isNextSeasonEqualCurrent()) {
        classes.push('c-seasons-day--middle');
      } else if (isPreviousSeasonEqualCurrent() && !isNextSeasonEqualCurrent()) {
        classes.push('c-seasons-day--end');
      } else if (!isPreviousSeasonEqualCurrent() && isNextSeasonEqualCurrent()) {
        classes.push('c-seasons-day--start');
      } else {
        classes.push('c-seasons-day--point');
      }

      classes.push('c-seasons-day--filled');

      return classes;
    }
    return null;
  };

  const getParentClass = (): string => {
    if (isPreviousSeasonEqualCurrent() && !isNextSeasonEqualCurrent()) {
      return 'c-seasons-day--parent-end';
    } if (!isPreviousSeasonEqualCurrent() && isNextSeasonEqualCurrent()) {
      return 'c-seasons-day--parent-start';
    } if (!isPreviousSeasonEqualCurrent() && !isNextSeasonEqualCurrent()) {
      return 'c-seasons-day--parent-point';
    }
    return '';
  };
  const isDateBetweenStartAndEnd = (day: number, month: number): boolean => {
    let { end, start }: { end: SeasonDate | null; start: SeasonDate | null } = selection.value;

    if (start && end) {
      if (start.day > end.day && (start.month > end.month || start.month === end.month)) {
        const temp = end;
        end = start;
        start = temp;
      }

      if (start.month < month) {
        return month <= end.month && day < end.day;
      }
      if (month < end.month) {
        return start.month <= month && start.day < day;
      }
      if (start.month === month && month === end.month) {
        return start.day < day && day < end.day;
      }
    }

    return false;
  };

  const getDatesBasedOnRange = (start: SeasonDate, end: SeasonDate): SeasonDate[] => {
    const LEAP_YEAR = 2020;
    const days = differenceInDays(new Date(LEAP_YEAR, end.month, end.day), new Date(LEAP_YEAR, start.month, start.day));
    const result: SeasonDate[] = [];

    let monthNumber = start.month;
    let { day } = start;

    for (let i = 0; i <= days; i += 1) {
      result.push({ month: monthNumber, day });
      day += 1;

      if (getDaysInMonth(new Date(LEAP_YEAR, monthNumber)) < day) {
        monthNumber += 1;
        day = 1;
      }
    }

    return result;
  };

  const getSelectionClasses = (): string[] => {
    const classes: string[] = [];

    if (selection.value.seasonCode) {
      if (selection.value.start && props.day === selection.value.start.day && props.month === selection.value.start.month) {
        classes.push('c-seasons-day--start-selection');
      }

      if (selection.value.end && props.day === selection.value.end.day && props.month === selection.value.end.month) {
        classes.push('c-seasons-day--end-selection');
      }

      if (isDateBetweenStartAndEnd(props.day, props.month)) {
        classes.push('c-seasons-day--middle-selection');
      }
    }

    return classes;
  };

  const updateCalendar = (payload: SeasonDateWithCode): void => {
    store.commit('views/vSeasons/updateCalendar', payload);
  };

  return {
    selection,
    updateCalendar,
    updateSelection,
    getClasses,
    getLabel,
    getActiveDay,
    getSelectionClasses,
    getParentClass,
    getDatesBasedOnRange,
  };
};
